import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import currency from 'currency.js';
import { Box } from '@tmap/mmm-style-guide/src/Box';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { Chip } from '@tmap/mmm-style-guide/src/Chip';
import { Card } from '@tmap/mmm-style-guide/src/Card';
import { Button } from '@tmap/mmm-style-guide/src/Button';
import {
  ArrowBack,
  InfoOutlined,
  ArrowRightAlt,
  ArrowDropDown,
  ArrowDropUp,
  OpenInNew,
  Remove,
} from '@tmap/mmm-style-guide/src/Icon';
import { Collapse } from '@tmap/mmm-style-guide/src/Transition';
import { Checkbox } from '@tmap/mmm-style-guide/src/Checkbox';
import { Divider } from '@tmap/mmm-style-guide/src/Divider';
import { styled } from '@tmap/mmm-style-guide/src/styled';
import { FormControlLabel } from '@tmap/mmm-style-guide/src/Form';
import NumberField from '@tmap/mmm-style-guide/src/fields/NumberField';

import useThunktank from '@tmap/mmm-core/redux/thunktank/useThunktank';
import locationLabelText from '@tmap/mmm-core/locationLabelText';
import useCurrentUser from '../hooks/useCurrentUser';
import useRequireAuth from '../hooks/useRequireAuth';
import { getGeoname } from '../actions/geonames';
import { updateUserProfile } from '../actions/userProfile';
import LocationAutocomplete from './locationAutocomplete';
import CommunityAutocomplete from './communityAutocomplete';

const NewChip = styled(Chip)(({ theme }) => ({
  marginLeft: theme.spacing(1),
  background: theme.palette.info.main,
  height: 'unset',
  fontWeight: 500,
  fontSize: '10px',
  [theme.breakpoints.down('sm')]: {
    marginLeft: 0,
  },
}));

const StatCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '8px',
  background: theme.palette.grey[100],
}));

const StatBadge = styled(Box, {
  shouldForwardProp: (p) => !['good', 'squashLeft'].includes(p),
})(({ theme, good, squashLeft }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: '5px',
  padding: '2px 13px',
  paddingLeft: squashLeft && '3px',
  background: good ? theme.palette.primary.main : theme.palette.purple.dark,
  color: good ? theme.palette.common.white : theme.palette.common.white,
  fontWeight: 600,
  fontSize: '12px',
  marginBottom: theme.spacing(2),
  '& svg': {
    color: good ? theme.palette.secondary.light : theme.palette.error.light,
  },
}));

const formatPercent = (value) => `${Math.abs(Math.round(value * 100))}%`;
const formatCurrency = (value) => currency(value, { precision: 0 }).format();

function CompareOverviewCard(props) {
  const { stat, place, location } = props;
  if (!place || !location) return null;
  const formattedStat = formatPercent(stat);
  // According to manual, a difference of <4% is not significant in composite
  if (stat <= -0.04) {
    return (
      <StatCard variant='outlined'>
        <StatBadge good>
          {location?.name}&ensp;<ArrowRightAlt />&ensp;{place.locationText}
        </StatBadge>
        <Typography variant='h5' fontWeight={600} color='primary.main'>
          {formattedStat} lower
        </Typography>
        <Typography variant='body2' color='textSecondary'>
          Make your move and enjoy a <Typography component='span' variant='body2' color='primary.veryDark' fontWeight={600}>lower</Typography> cost of living in {place.locationText}.
        </Typography>
      </StatCard>
    );
  }
  if (stat >= 0.04) {
    return (
      <StatCard variant='outlined'>
        <StatBadge>
          {location?.name}&ensp;<ArrowRightAlt />&ensp;{place.locationText}
        </StatBadge>
        <Typography variant='h5' fontWeight={600} color='textPrimary'>
          {formattedStat} higher
        </Typography>
        <Typography variant='body2' color='textSecondary'>
          Unfortunately, there&apos;s a <Typography component='span' variant='body2' color='error' fontWeight={600}>higher</Typography> cost of living in {place.locationText}.
        </Typography>
      </StatCard>
    );
  }
  return (
    <StatCard variant='outlined'>
      <StatBadge good>
        {location?.name}&ensp;<ArrowRightAlt />&ensp;{place.locationText}
      </StatBadge>
      <Typography variant='h5' fontWeight={600} color='primary.main'>
        About the Same
      </Typography>
      <Typography variant='body2' color='textSecondary'>
        Make your move and enjoy a similar cost of living in {place.locationText}.
      </Typography>
    </StatCard>
  );
}

function CompareIncomeCard(props) {
  const {
    stat,
    place,
    location,
    salary,
  } = props;
  if (!place || !location) return null;
  const diff = salary * -stat;
  const salaryDiff = formatCurrency(Math.abs(diff));
  const currentSalary = formatCurrency(salary);
  const newSalary = formatCurrency(salary + diff);
  const good = stat < 0;
  // According to manual, a difference of <4% is not significant in composite
  if (stat >= 0.04 || stat <= -0.04) {
    return (
      <StatCard variant='outlined'>
        <StatBadge good={good} squashLeft>
          <ArrowDropUp /> {good ? '+' : '-'} {salaryDiff} per year
        </StatBadge>
        <Typography variant='body2' color='textSecondary'>
          Your {currentSalary} salary spent in {place.locationText} is comparable to making <Typography component='span' variant='body2' color={good ? 'primary.veryDark' : 'error'} fontWeight={600}>{newSalary}</Typography> in {location.name}.
        </Typography>
      </StatCard>
    );
  }
  return (
    <StatCard variant='outlined'>
      <StatBadge good squashLeft>
        <ArrowDropUp /> + $0 per year
      </StatBadge>
      <Typography variant='body2' color='textSecondary'>
        Your {currentSalary} salary
        spent in {place.locationText} is worth about the same as in {location.name}.
      </Typography>
    </StatCard>
  );
}

function CompareHousingCard(props) {
  const { stat, place, location } = props;
  if (!place || !location) return null;
  const formattedStat = formatPercent(stat);
  const enthusiasm = {
    5: 'Not bad.',
    10: 'Great!',
    15: 'Awesome!',
  }[Math.min(15, Math.floor(parseInt(formattedStat, 10) / 5) * 5)];
  // According to manual, a difference of <5% is not significant in indexes
  if (stat <= -0.05) {
    return (
      <StatCard variant='outlined'>
        <StatBadge good squashLeft>
          <ArrowDropDown /> {formattedStat} lower housing costs
        </StatBadge>
        <Typography variant='body2' color='textSecondary'>
          Housing in {place.locationText} is more affordable by {formattedStat}.
          {enthusiasm && (
            <Typography component='span' variant='body2' color='primary.veryDark' fontWeight={600}>
              {' '}{enthusiasm}
            </Typography>
          )}
        </Typography>
      </StatCard>
    );
  }
  if (stat >= 0.05) {
    return (
      <StatCard variant='outlined'>
        <StatBadge squashLeft>
          <ArrowDropUp /> {formattedStat} higher housing costs
        </StatBadge>
        <Typography variant='body2' color='textSecondary'>
          Housing in {place.locationText} is less affordable by {formattedStat}.
        </Typography>
      </StatCard>
    );
  }

  return (
    <StatCard variant='outlined'>
      <StatBadge good squashLeft>
        <Remove sx={{ padding: '2px' }} /> Similar housing costs
      </StatBadge>
      <Typography variant='body2' color='textSecondary'>
        Housing in {place.locationText} is similarly affordable.
      </Typography>
    </StatCard>
  );
}

function CompareOtherText(props) {
  const { stat } = props;
  const good = stat < 0;
  // According to manual, a difference of <5% is not significant in indexes
  if (stat >= 0.05 || stat <= -0.05) {
    return (
      <Typography variant='body2' color={good ? 'secondary' : 'error'}>
        {formatPercent(stat)} {good ? 'lower' : 'higher'}
      </Typography>
    );
  }
  return (
    <Typography variant='body2' color='secondary'>
      About the same
    </Typography>
  );
}

function CostOfLivingWidget(props) {
  const {
    place,
    header = 'Cost of Living Comparison',
  } = props;
  const user = useCurrentUser();
  const dispatch = useDispatch();
  const { requireAuth, isAuthenticated } = useRequireAuth();
  const useThunk = useThunktank('colWidget');

  const [salary, setSalary] = useState('');
  const handleSalaryChange = (value) => {
    setSalary(value);
  };

  const [location, setLocation] = useState(null);
  const [origin, setOrigin] = useState(place || null);
  const [prefetchedLocation, fetchLocation, resetPrefetch] = useThunk(getGeoname);
  useEffect(() => {
    if (!location && prefetchedLocation.data?.geoname) {
      setLocation(prefetchedLocation.data.geoname);
      resetPrefetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, prefetchedLocation.data]);

  const [saveProfile, setSaveProfile] = useState(true);
  const handleCheckChange = (e) => {
    setSaveProfile(e.target.checked);
  };

  const [submitted, setSubmitted] = useState(false);
  const handleShowData = () => {
    if (isAuthenticated && saveProfile) {
      dispatch(updateUserProfile({
        'personalInfo.geoname_id': location.geoname_id,
        'household.income': salary,
      }));
    } else {
      window.localStorage.setItem('costOfLivingForceLocal', !saveProfile);
      window.localStorage.setItem('costOfLivingWidgetSalary', String(salary));
      window.localStorage.setItem('costOfLivingWidgetLocation', JSON.stringify(location.geoname_id));
    }
    setSubmitted(true);
  };
  const handleHideData = () => {
    window.localStorage.removeItem('costOfLivingForceLocal');
    window.localStorage.removeItem('costOfLivingWidgetSalary');
    window.localStorage.removeItem('costOfLivingWidgetLocation');
    setSubmitted(false);
  };

  useEffect(() => {
    const forceLocal = Boolean(JSON.parse(window.localStorage.getItem('costOfLivingForceLocal')));
    const savedSalary = window.localStorage.getItem('costOfLivingWidgetSalary');
    const savedLocation = window.localStorage.getItem('costOfLivingWidgetLocation');
    const profileSalary = user?.household?.income;
    const localSalary = savedSalary ? String(savedSalary) : undefined;
    const profileLocation = user?.personalInfo?.geoname_id;
    const localLocation = savedLocation ? parseInt(savedLocation, 10) : undefined;
    const [preloadSalary, preloadLocation] = forceLocal ? [
      localSalary || profileSalary,
      localLocation || profileLocation,
    ] : [
      profileSalary || localSalary,
      profileLocation || localLocation,
    ];
    if (forceLocal) {
      setSaveProfile(false);
    }
    if (preloadSalary) {
      setSalary(preloadSalary);
    }
    if (preloadLocation) {
      fetchLocation(preloadLocation);
    }
    if (preloadSalary && preloadLocation && place) {
      setSubmitted(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const [showLearn, setShowLearn] = useState(false);
  const handleShowLearn = () => {
    setShowLearn(true);
  };
  const handleHideLearn = () => {
    setShowLearn(false);
  };

  const fromColiData = location?.coliData;
  const toColiData = origin?.locationGeo?.coliData;
  const calcDiff = (from, to) => ((to - from) / from);
  const coliStats = (fromColiData && toColiData) ? {
    composite: calcDiff(fromColiData.coli, toColiData.coli),
    housing: calcDiff(fromColiData.housing, toColiData.housing),
    groceries: calcDiff(fromColiData.groceries, toColiData.groceries),
    utilities: calcDiff(fromColiData.utilities, toColiData.utilities),
    transportation: calcDiff(fromColiData.transportation, toColiData.transportation),
    healthCare: calcDiff(fromColiData.healthCare, toColiData.healthCare),
    miscellaneous: calcDiff(fromColiData.miscellaneous, toColiData.miscellaneous),
  } : {};

  const valid = salary > 0 && location && fromColiData;
  const showData = Boolean(submitted && valid);

  return (
    <Grid container spacing={{ xs: 4, md: 8 }}>
      <Grid item xs={12} md={6}>
        <Typography variant='h4' fontWeight={700} mb={1}>
          {header}
          <Box sx={{ display: { xs: 'block', sm: 'inline' } }}>
            <NewChip label='New!' color='info' size='small' />
          </Box>
        </Typography>
        <Collapse in={!showLearn}>
          <Box display='contents'>
            <Typography variant='body1' color='textSecondary' mb={3}>
              See how your current location {showData ? ` of ${location.name}, ${location.state} ` : ''}
              compares to the cost of living
              {place
                ? ` in ${locationLabelText(origin?.locationText, origin?.regionText)} ${showData ? '' : ' by sharing a few details'}.`
                : ' in our partner communities.'}
              {!isAuthenticated && (
                <>
                  <br /><br />
                  Want us to save your info for easy browsing?
                  Sign up today and we&apos;ll automatically load cost of
                  living data for you on each page across all devices.
                </>
              )}
            </Typography>
            <Grid container spacing={{ xs: 2, sm: 4 }} alignItems='center'>
              {!isAuthenticated && (
                <Grid item xs={12} sm='auto'>
                  <Button variant='contained' color='convert' onClick={() => requireAuth()} sx={{ width: { xs: '100%', sm: '120px' } }}>
                    Sign Up
                  </Button>
                </Grid>
              )}
              <Grid item>
                <Button color='secondary' size='small' endIcon={<InfoOutlined />} sx={{ marginLeft: { xs: -0.25, sm: -0.5 } }} onClick={handleShowLearn}>
                  Learn about the data
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
        <Collapse in={showLearn}>
          <Typography variant='body2' color='textSecondary' mb={3}>
            MakeMyMove sources all Cost of Living Data from COLI,
            published by the Council for Community and Economic Research (C2ER),
            without any modification or bias.
            <br /><br />
            COLI data is recognized by the U.S. Census Bureau,
            U.S. Bureau of Labor Statistics, and CNN Money.
            The data and methodology are described in detail
            and completely transparent to users, found in the COLI Manual.
            Both data and methodology are reviewed by an Advisory Board
            composed of academic researchers and government officials.
            The Cost of Living Index is referenced in the Census Bureau&apos;s
            Statistical Abstract of the U.S.
            <br /><br />
            According to the COLI Manual, composite differences of &lt;4%
            and categorical differences of &lt;5%
            are not statistically significant and should not be treated as necessarily accurate.
          </Typography>
          <Grid container spacing={2} alignItems='center'>
            <Grid item xs>
              <Button color='convert' startIcon={<ArrowBack />} sx={{ marginLeft: -0.5 }} onClick={handleHideLearn}>
                Back
              </Button>
            </Grid>
            <Grid item>
              <Button href='https://www.coli.org/about' target='_blank' variant='contained' endIcon={<OpenInNew />} sx={{ marginLeft: 1 }} onClick={handleHideLearn}>
                About COLI
              </Button>
            </Grid>
          </Grid>
        </Collapse>
      </Grid>
      <Grid item xs={12} md={6}>
        <Collapse in={!showData}>
          <Grid container spacing={3} pt={0.75} mt={{ xs: 0, md: 4 }}>
            <Grid item xs={12}>
              <NumberField label='Current Base Salary' valueType='currency' fullWidth autoComplete='off' input={{ value: salary, onChange: handleSalaryChange }} />
            </Grid>
            <Grid item xs={12} sm={place ? 12 : 6}>
              <LocationAutocomplete value={location} setValue={setLocation} useThunk={useThunk} />
            </Grid>
            {!place && (
              <Grid item xs={12} sm={6}>
                <CommunityAutocomplete value={origin} setValue={setOrigin} useThunk={useThunk} label='Moving To' />
              </Grid>
            )}
            {(location && !fromColiData) ? (
              <Grid item xs={12}>
                <Typography component='div' variant='body2' color='error' textAlign='center' mb={0.25}>
                  Whoops, this location doesn&apos;t have any cost of living data yet.<br />
                  Please try a nearby location instead.
                </Typography>
              </Grid>
            ) : (
              isAuthenticated && (
                <Grid item xs={12}>
                  <FormControlLabel control={<Checkbox checked={saveProfile} onChange={handleCheckChange} />} label='Save this information to my profile' />
                </Grid>
              )
            )}
            <Grid item xs={12}>
              <Button variant='contained' color='primary' fullWidth onClick={handleShowData} disabled={!valid}>
                See the Data
              </Button>
            </Grid>
          </Grid>
        </Collapse>
        <Collapse in={showData}>
          <Box mt={{ xs: 2, md: 4 }} ml={{ xs: 1, md: 0 }}>
            <Typography variant='body2' color='textSecondary'>Based on your salary of</Typography>
            <Typography variant='h6' component='div' fontWeight={500}>
              {currency(salary, { precision: 0 }).format()}
            </Typography>
            <Typography variant='body2' color='textSecondary'>
              in {location?.name}, {location?.state_abbreviation}
            </Typography>
            <Button size='small' onClick={handleHideData} sx={{ marginLeft: -0.5 }}>
              Change
            </Button>
          </Box>
        </Collapse>
      </Grid>
      <Grid item xs={12} md={6}>
        <Collapse in={showData}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <CompareOverviewCard
                stat={coliStats.composite}
                place={origin}
                location={location}
              />
            </Grid>
            <Grid item xs={12}>
              <CompareIncomeCard
                stat={coliStats.composite}
                place={origin}
                location={location}
                salary={parseInt(salary || 0, 10)}
              />
            </Grid>
            <Grid item xs={12}>
              <CompareHousingCard
                stat={coliStats.housing}
                place={origin}
                location={location}
              />
            </Grid>
          </Grid>
        </Collapse>
      </Grid>
      <Grid item xs={12} md={6}>
        <Collapse in={showData}>
          <Box display='contents'>
            <Typography variant='subtitle1' fontWeight={500} mb={1}>Other Price Comparisons</Typography>
            <Typography variant='body2' color='textSecondary'>Check out all the ways your money compares in {origin?.locationText}.</Typography>
            <Grid container spacing={2} my={2}>
              <Grid item xs><Typography variant='body2'>Groceries</Typography></Grid>
              <Grid item><CompareOtherText stat={coliStats.groceries} /></Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs><Typography variant='body2'>Utilities</Typography></Grid>
              <Grid item><CompareOtherText stat={coliStats.utilities} /></Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs><Typography variant='body2'>Transportation</Typography></Grid>
              <Grid item><CompareOtherText stat={coliStats.transportation} /></Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs><Typography variant='body2'>Healthcare</Typography></Grid>
              <Grid item><CompareOtherText stat={coliStats.healthCare} /></Grid>
              <Grid item xs={12}><Divider /></Grid>
              <Grid item xs><Typography variant='body2'>Misc</Typography></Grid>
              <Grid item><CompareOtherText stat={coliStats.miscellaneous} /></Grid>
              <Grid item xs={12}><Divider /></Grid>
            </Grid>
          </Box>
        </Collapse>
      </Grid>
    </Grid>
  );
}

export default CostOfLivingWidget;
